import { useMemo, useState } from 'react';
import { skills, levels, getLessons } from '../content/skills.js';
import { stories } from '../content/stories.js';
import {
  useProgress,
  setActiveSkill,
  getSkillStrength,
} from '../store/progress.js';
import { Icons } from './Icons.jsx';
import { MajaTip } from './Mascot.jsx';
import TopStatsBar from './TopStatsBar.jsx';
import BottomTabs from './BottomTabs.jsx';
import { tipFor } from '../content/majaTips.js';
import { BALL } from '../theme.js';
import { t } from '../i18n.js';

const T = BALL;

function pickFirstIncompleteSkillId(progress) {
  for (const s of skills) {
    const completed = progress.skillProgress[s.id]?.completed || 0;
    const total = getLessons(s).length;
    if (completed < total) return s.id;
  }
  return skills[0].id;
}

function nodeIconFor(lesson) {
  if (lesson.isReview) return 'trophy';
  return 'book';
}

function NodeBubble({ status, icon, lang, label, onClick, theme = T }) {
  const palette = {
    done:    { fill: theme.primary,  deep: theme.primaryDeep, ring: 'rgba(255,122,69,0.25)' },
    current: { fill: theme.gold,     deep: theme.goldDeep,    ring: 'rgba(255,200,87,0.4)'  },
    locked:  { fill: theme.locked,   deep: theme.lockedInk,   ring: 'transparent' },
    bonus:   { fill: theme.blue,     deep: theme.blueDeep,    ring: 'rgba(45,91,255,0.3)' },
  }[status];

  const renderIcon = () => {
    if (status === 'locked') return Icons.lock();
    if (icon === 'ribbon')   return Icons.ribbon();
    if (icon === 'chest')    return Icons.chest();
    if (icon === 'trophy')   return Icons.trophy();
    return Icons.book();
  };

  return (
    <div style={{
      width: 80, display: 'flex', flexDirection: 'column', alignItems: 'center',
      position: 'relative',
    }}>
      {status === 'current' && (
        <>
          <div style={{
            position: 'absolute', top: -10, left: -10, width: 100, height: 100,
            borderRadius: '50%', background: palette.ring,
            animation: 'pulse 2s ease-out infinite',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: -42, background: theme.bgAlt,
            border: `2px solid ${theme.hairline}`, padding: '4px 10px',
            borderRadius: 8, fontSize: 11, fontWeight: 900, color: theme.gold,
            letterSpacing: '0.08em', boxShadow: `0 2px 0 ${theme.hairline}`,
            whiteSpace: 'nowrap',
          }}>
            {t('start', lang).toUpperCase()}
            <div style={{
              position: 'absolute', bottom: -6, left: '50%', marginLeft: -5,
              width: 0, height: 0,
              borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
              borderTop: `6px solid ${theme.hairline}`,
            }} />
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={status === 'locked'}
        style={{
          appearance: 'none', border: 'none', padding: 0,
          width: 76, height: 76, borderRadius: '50%',
          background: palette.fill,
          boxShadow: status === 'locked' ? `0 4px 0 ${palette.deep}` : `0 6px 0 ${palette.deep}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: status === 'locked' ? 'not-allowed' : 'pointer',
          position: 'relative', zIndex: 1,
        }}
      >
        {renderIcon()}
      </button>
      {label && status !== 'current' && (
        <div style={{
          marginTop: 8, fontSize: 11, fontWeight: 700,
          color: status === 'locked' ? theme.lockedInk : theme.inkSoft,
          letterSpacing: '0.02em', textAlign: 'center', maxWidth: 110,
        }}>
          {label}
        </div>
      )}
    </div>
  );
}

function UnitPicker({ onClose, onPick, currentId, lang }) {
  const progress = useProgress();
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 50,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.bgAlt, width: '100%', maxWidth: 480,
        borderRadius: '20px 20px 0 0',
        maxHeight: '80vh', overflowY: 'auto',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'center', padding: '10px 0 4px',
        }}>
          <div style={{ width: 40, height: 4, background: T.hairline, borderRadius: 2 }} />
        </div>
        <div style={{ padding: '8px 18px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>
            {lang === 'en' ? 'Pick a unit' : 'Välj enhet'}
          </h2>
          <button type="button" onClick={onClose} style={{
            appearance: 'none', border: 'none', background: 'none', cursor: 'pointer',
          }}>
            {Icons.close(T.inkSoft)}
          </button>
        </div>

        {levels.map((lvl) => (
          <div key={lvl.id} style={{ padding: '8px 18px 18px' }}>
            <div style={{
              fontSize: 11, fontWeight: 900, color: T.inkSoft,
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8,
            }}>
              {lvl.name[lang]}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {skills.filter((s) => s.level === lvl.id).map((s) => {
                const completed = progress.skillProgress[s.id]?.completed || 0;
                const total = getLessons(s).length;
                const isDone = completed >= total;
                const isCurrent = s.id === currentId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onPick(s.id)}
                    style={{
                      appearance: 'none', cursor: 'pointer',
                      background: isCurrent ? '#FFF1E6' : T.bgAlt,
                      border: `2px solid ${isCurrent ? T.primary : T.hairline}`,
                      borderRadius: 14, padding: '12px 14px',
                      boxShadow: `0 3px 0 ${isCurrent ? T.primary : T.hairline}`,
                      display: 'flex', alignItems: 'center', gap: 12,
                      textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 42, height: 42, borderRadius: 10,
                      background: s.color, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 22,
                    }}>
                      {s.emoji}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 900, color: T.ink, fontSize: 15 }}>
                        {s.name[lang]}
                      </div>
                      <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 2 }}>
                        {completed}/{total} · {s.blurb[lang]}
                      </div>
                    </div>
                    {isDone && <div style={{ color: T.mint, fontSize: 20, fontWeight: 900 }}>✓</div>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoriesRail({ lang, onPick, progress }) {
  return (
    <div style={{ padding: '8px 16px 0' }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: T.inkSoft,
        letterSpacing: '0.12em', textTransform: 'uppercase', margin: '4px 4px 8px',
      }}>
        {lang === 'en' ? 'Stories' : 'Berättelser'}
      </div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
        {stories.map((s) => {
          const required = skills.find((sk) => sk.id === s.unlocksAfterSkill);
          const strength = required ? getSkillStrength(required) : 5;
          const unlocked = strength >= 0.5;
          const done = progress.storyProgress?.[s.id]?.completed;
          const bg = done ? T.mint : unlocked ? T.blue : T.locked;
          const deep = done ? T.mintDeep : unlocked ? T.blueDeep : T.lockedInk;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => unlocked && onPick(s)}
              disabled={!unlocked}
              style={{
                appearance: 'none', border: 'none',
                background: 'transparent', cursor: unlocked ? 'pointer' : 'not-allowed',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                minWidth: 88, padding: 0, fontFamily: 'inherit',
              }}
            >
              <div style={{
                width: 72, height: 72, borderRadius: 18,
                background: bg, boxShadow: `0 5px 0 ${deep}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, color: '#fff',
                opacity: unlocked ? 1 : 0.6,
              }}>
                {done ? '✓' : unlocked ? s.emoji : '🔒'}
              </div>
              <div style={{
                fontSize: 12, fontWeight: 800,
                color: unlocked ? T.ink : T.lockedInk,
                textAlign: 'center', lineHeight: 1.2,
                maxWidth: 92,
              }}>
                {s.title[lang]}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Home({ onStartLesson, onStartSpeedRound, onStartStory, onOpenSettings, onTabChange }) {
  const progress = useProgress();
  const lang = progress.sourceLang;
  const [pickerOpen, setPickerOpen] = useState(false);

  const activeSkillId = progress.activeSkillId || pickFirstIncompleteSkillId(progress);
  const activeSkill = skills.find((s) => s.id === activeSkillId) || skills[0];
  const lessons = useMemo(() => getLessons(activeSkill), [activeSkill]);
  const completed = progress.skillProgress[activeSkill.id]?.completed || 0;
  const sectionIndex = activeSkill.level === 'A1' ? 1 : 2;
  const unitIndex = skills.filter((s) => s.level === activeSkill.level).indexOf(activeSkill) + 1;

  const nodes = lessons.map((lesson, i) => {
    let status = 'locked';
    if (i < completed) status = 'done';
    else if (i === completed) status = 'current';

    const firstItem = lesson.items[0];
    const firstWord = firstItem?.hr?.split(' ')[0];
    const label = lesson.isReview
      ? (lang === 'en' ? 'Review' : 'Repetition')
      : firstWord;

    return {
      i,
      status,
      icon: nodeIconFor(lesson),
      label,
      lesson,
    };
  });

  const offsetX = (i) => Math.sin(i * 0.9) * 90;
  const pathHeight = Math.max(420, nodes.length * 110 + 80);

  return (
    <div className="app-shell">
      <TopStatsBar
        streak={progress.streak}
        gems={progress.gems}
        theme={T}
        onSettings={onOpenSettings}
      />

      <div className="app-scroll">
        {/* Unit banner */}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          style={{
            appearance: 'none', cursor: 'pointer', border: 'none',
            margin: '14px 16px 0', padding: '14px 18px',
            background: T.primary, borderRadius: 14,
            boxShadow: `0 4px 0 ${T.primaryDeep}`,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: 'calc(100% - 32px)', textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', opacity: 0.85 }}>
              SECTION {sectionIndex} · UNIT {unitIndex}
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, marginTop: 2 }}>
              {activeSkill.name[lang]}
            </div>
          </div>
          <div style={{
            width: 38, height: 38, borderRadius: 8,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
          }}>
            {activeSkill.emoji}
          </div>
        </button>

        {/* Speed-round entry — unlocks after 3 completed lessons */}
        {progress.totalLessons >= 3 && onStartSpeedRound && (
          <div style={{ padding: '12px 16px 0' }}>
            <button
              type="button"
              onClick={onStartSpeedRound}
              style={{
                appearance: 'none', cursor: 'pointer', border: 'none',
                width: '100%', padding: '12px 16px',
                background: T.gold, color: T.ink,
                borderRadius: 999, boxShadow: `0 4px 0 ${T.goldDeep}`,
                fontFamily: 'inherit', fontWeight: 900, fontSize: 14,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 18 }}>⚡</span>
              {t('speedRoundTitle', lang)}
              {progress.speedRoundBest > 0 && (
                <span style={{
                  background: 'rgba(0,0,0,0.12)', padding: '2px 8px',
                  borderRadius: 999, fontSize: 12,
                }}>
                  {t('best', lang)}: {progress.speedRoundBest}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Stories rail */}
        {onStartStory && (
          <StoriesRail lang={lang} onPick={onStartStory} progress={progress} />
        )}

        {/* Maja tip */}
        <div style={{ padding: '12px 16px 0' }}>
          <MajaTip theme={T} mood="happy" label="MAJA KAŽE">
            {tipFor(activeSkill.id, lang)}
          </MajaTip>
        </div>

        {/* Winding path */}
        <div style={{ position: 'relative', padding: '20px 0 24px', minHeight: pathHeight }}>
          <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: pathHeight, pointerEvents: 'none' }}
               viewBox={`0 0 390 ${pathHeight}`} preserveAspectRatio="xMidYMin meet">
            <path
              d={nodes.map((n, i) => {
                const x = 195 + offsetX(n.i);
                const y = n.i * 110 + 60;
                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
              }).join(' ')}
              stroke={T.hairline} strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="2 14"
            />
          </svg>
          {nodes.map((n) => {
            // Node absolute position relative to the 390-wide design grid;
            // the path SVG above uses the same coordinate system.
            const leftPct = ((195 + offsetX(n.i)) / 390) * 100;
            const top = n.i * 110 + 60;
            return (
              <div
                key={n.i}
                style={{
                  position: 'absolute',
                  left: `calc(${leftPct}% - 40px)`,
                  top: top - 40,
                }}
              >
                <NodeBubble
                  status={n.status}
                  icon={n.icon}
                  label={n.label}
                  lang={lang}
                  onClick={() => onStartLesson(activeSkill, n.lesson)}
                  theme={T}
                />
              </div>
            );
          })}
        </div>
      </div>

      <BottomTabs active="home" onSelect={onTabChange} theme={T} />

      {pickerOpen && (
        <UnitPicker
          currentId={activeSkillId}
          lang={lang}
          onClose={() => setPickerOpen(false)}
          onPick={(id) => {
            setActiveSkill(id);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
